import axios from 'axios'
import appConfig from 'configs/app.config'
import { TOKEN_TYPE, REQUEST_HEADER_AUTH_KEY } from 'constants/api.constant'
import { PERSIST_STORE_NAME } from 'constants/app.constant'
import deepParseJson from 'utils/deepParseJson'
import store from '../store'
import { onSignOutSuccess } from '../store/auth/sessionSlice'

const unauthorizedCode = [401]

// Note: the instance-level Authorization header was previously set from
// localStorage("access_token") at module-load time and never refreshed, so
// after login the header held the *empty* pre-login value on the axios
// instance for the lifetime of the page. The request interceptor below
// overrides `Authorization` per-request from the redux-persist token, which
// is the actual source of truth.
const BaseService = axios.create({
    timeout: 60000,
    mode: 'no-cors',
    baseURL: appConfig.apiPrefix,
    headers: {
        "Access-Control-Allow-Headers": "origin, content-type, accept",
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET,POST,PATCH,PUT,DELETE,OPTIONS',
    }
})

BaseService.interceptors.request.use(config => {
    // The token lives in two places for historical reasons:
    //   1. redux-persist state at PERSIST_STORE_NAME.auth.session.token
    //   2. localStorage.access_token (set by useAuth on sign-in)
    // The request interceptor prefers redux-persist and falls back to the
    // legacy key so a page reload before rehydration still carries the token.
    let accessToken = ''
    try {
        const rawPersistData = localStorage.getItem(PERSIST_STORE_NAME)
        const persistData = deepParseJson(rawPersistData)
        accessToken = persistData?.auth?.session?.token || ''
    } catch (e) {
        accessToken = ''
    }
    if (!accessToken) {
        accessToken = localStorage.getItem('access_token') || ''
    }

    if (accessToken) {
        config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`
    }

    return config
}, error => {
    return Promise.reject(error)
})

BaseService.interceptors.response.use(
    response => response,
    error => {

        const { response } = error

        if (response && unauthorizedCode.includes(response.status)) {
            // Clear both storage locations so a stale token cannot linger
            // after the server has said "unauthorized". Session slice's
            // signedIn flag drives the auth guard's redirect to sign-in.
            try {
                localStorage.removeItem('access_token')
                localStorage.removeItem('userDetails')
                localStorage.removeItem('login_time')
            } catch (e) { /* ignore storage errors */ }
            store.dispatch(onSignOutSuccess())
        }

        return Promise.reject(error)
    }
)

export default BaseService